import React, { useState, useEffect } from 'react';
import { RootState } from 'redux/root.reducer';
import { CFCProps, DBYD_1100_URL } from 'types';
import {
  Loading,
  Accordion,
  AccordionItem,
  InlineLoading,
  Button,
  Link
} from 'carbon-components-react';
import { Attachment16 } from '@carbon/icons-react';
import { connect, useDispatch } from 'react-redux';
import { convertEmaliStatusTolabel } from 'appConstants/emails';
import { resendEmailStart, resendEmailSuccess } from 'redux/emails';
import {
  toLocalDateTimeFormatWithSeconds,
  getEmailAttachment
} from 'utilities/helper.util';

export const DetailsPanel: React.FC<Props> = ({
  emailDetail,
  resending,
  resendingToOther,
  resendedId,
  loading,
  isRefferal,
  currentJobId,
  isEventOpen,
  isAttachmentOpen,
  onClose,
  handleSendToOther,
  disableEmailResend
}) => {
  const dispatch = useDispatch();
  const daysDifferenceThreshold = 90;
  const [isAPIUpload, setIsAPIUpload] = useState<boolean>(false);

  useEffect(() => {
    emailDetail && setIsAPIUpload(!emailDetail.Response?.InboundEmail);
  }, [emailDetail]);

  useEffect(() => {
    resendedId &&
      setTimeout(() => dispatch(resendEmailSuccess(undefined)), 2000);
  }, [dispatch, resendedId]);

  return (
    <div
      id="ao-emails-detail-panel"
      data-testid="ao-emails-detail-panel"
      className="panel--body"
    >
      {loading ? (
        <div className="loader--wrapper">
          <Loading description="Active loading indicator" withOverlay={false} />
        </div>
      ) : (
        <>
          <div className="panel--body-job-id">Job {currentJobId}</div>
          <hr className="section--divider" />
          {emailDetail && (
            <>
              <h2 className="panel--body-sub-heading">
                {isRefferal ? 'Status' : 'Relay status'}:{' '}
                {convertEmaliStatusTolabel(emailDetail.status)}
              </h2>
              {isRefferal ? (
                <div className="panel--body-content">
                  <div>
                    <span className="label">Sent:</span>{' '}
                    {toLocalDateTimeFormatWithSeconds(emailDetail.createdAt)}
                  </div>
                  <div>
                    <span className="label">Subject:</span>{' '}
                    {emailDetail.subject}
                  </div>
                  <div>
                    <span className="label">Recipient:</span> {emailDetail.to}
                  </div>
                </div>
              ) : (
                <>
                  {isAPIUpload ? (
                    <>
                      <div className="panel--body-content">
                        <div className="title">API Upload</div>
                        <div>
                          <span className="label">Received:</span>{' '}
                          {toLocalDateTimeFormatWithSeconds(
                            emailDetail.Response?.createdAt
                          )}
                        </div>
                        <div>
                          <span className="label">From:</span>{' '}
                          {emailDetail.Response?.User.firstName}{' '}
                          {emailDetail.Response?.User.lastName}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="panel--body-content">
                        <div className="title">Inbound</div>
                        <div>
                          <span className="label">Received:</span>{' '}
                          {toLocalDateTimeFormatWithSeconds(
                            emailDetail.Response?.InboundEmail?.createdAt
                          )}
                        </div>
                        <div>
                          <span className="label">From:</span>{' '}
                          {emailDetail.Response?.InboundEmail?.from}
                        </div>
                        <div>
                          <span className="label">Subject:</span>{' '}
                          {emailDetail.Response?.InboundEmail?.subject}
                        </div>
                        <div>
                          <span className="label">To:</span>{' '}
                          {emailDetail.Response?.InboundEmail?.to}
                        </div>
                      </div>
                    </>
                  )}
                  <div className="panel--body-content">
                    <div className="title">Outbound</div>
                    <div>
                      <span className="label">Sent:</span>{' '}
                      {toLocalDateTimeFormatWithSeconds(emailDetail.createdAt)}
                    </div>
                    <div>
                      <span className="label">Subject:</span>{' '}
                      {emailDetail.subject}
                    </div>
                    <div>
                      <span className="label">To:</span> {emailDetail.to}
                    </div>
                  </div>
                </>
              )}
              <Accordion>
                {emailDetail.Events?.length > 0 && (
                  <AccordionItem
                    title="More delivery events"
                    open={isEventOpen.current}
                    onHeadingClick={({ isOpen }) => {
                      isEventOpen.current = isOpen;
                    }}
                  >
                    <div className="events">
                      {emailDetail.Events.map((event: any) => (
                        <div key={`event-${event.id}`}>
                          {toLocalDateTimeFormatWithSeconds(event.createdAt)}{' '}
                          {convertEmaliStatusTolabel(event.type)}
                        </div>
                      ))}
                    </div>
                    <div className="events-help-link">
                      <Link
                        onClick={() =>
                          window.open(
                            DBYD_1100_URL.EMAIL_DELIVERY_EVENTS,
                            '_blank'
                          )
                        }
                      >
                        Delivery events help
                      </Link>
                    </div>
                  </AccordionItem>
                )}
                {emailDetail.Attachments?.length > 0 && (
                  <AccordionItem
                    title="Attachments"
                    open={isAttachmentOpen.current}
                    onHeadingClick={({ isOpen }) => {
                      isAttachmentOpen.current = isOpen;
                    }}
                  >
                    <div className="attachments">
                      {emailDetail.Attachments.map((file: any) => (
                        <Link
                          key={`file-${file.id}`}
                          onClick={() =>
                            getEmailAttachment({
                              url: file.url,
                              attachmentName: file.name
                            })
                          }
                        >
                          <Attachment16 /> {file.name}
                        </Link>
                      ))}
                    </div>
                  </AccordionItem>
                )}
              </Accordion>
              <div className="panel--body-controls">
                <div>
                  {resendingToOther === undefined &&
                  (resending || resendedId) ? (
                    <InlineLoading
                      description={resendedId ? 'Sent' : 'Sending'}
                      status={resendedId ? 'finished' : 'active'}
                    />
                  ) : (
                    <Button
                      kind="tertiary"
                      size="small"
                      onClick={() =>
                        dispatch(resendEmailStart({ emailId: emailDetail.id }))
                      }
                      disabled={disableEmailResend}
                      title={
                        disableEmailResend
                          ? `Resending / forwarding not available for email older than ${daysDifferenceThreshold} days`
                          : 'Resend email'
                      }
                    >
                      Resend email
                    </Button>
                  )}
                </div>
                <div>
                  <Link onClick={() => onClose()}>Close</Link>
                </div>
                <div className="send-to-other-link">
                  {resendingToOther !== undefined &&
                  (resendingToOther || resendedId) ? (
                    <InlineLoading
                      description={resendedId ? 'Sent' : 'Sending'}
                      status={resendedId ? 'finished' : 'active'}
                    />
                  ) : (
                    <Link
                      disabled={disableEmailResend}
                      onClick={
                        !disableEmailResend
                          ? () => handleSendToOther(emailDetail.id)
                          : () => {}
                      }
                      title={
                        disableEmailResend
                          ? `Resending / forwarding not available for email older than ${daysDifferenceThreshold} days`
                          : 'Send to other'
                      }
                      className={disableEmailResend ? 'link-disabled' : ''}
                    >
                      Send to other
                    </Link>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = ({ emailCache }: RootState) => ({
  resending: emailCache.resending,
  resendingToOther: emailCache.resendingToOther,
  resendedId: emailCache.resendedId,
  emailDetail: emailCache.emailDetail,
  loading: emailCache.loading
});

const connector = connect(mapStateToProps);

type OwnProps = {
  isRefferal: boolean;
  onClose: Function;
  currentJobId?: number;
  isEventOpen: React.MutableRefObject<boolean>;
  isAttachmentOpen: React.MutableRefObject<boolean>;
  handleSendToOther: Function;
  disableEmailResend: boolean;
};

type Props = CFCProps<typeof connector> & OwnProps;

export default React.memo(connector(DetailsPanel));
